import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ClientService} from '@app/admin/client.service';
import {deserialize} from '@app/serializable';

import {BehaviorSubject, Observable, ReplaySubject} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

export enum Role {
    User,
    Admin,
    Manager,
    Billing,
    Guest,
}

export const ROLES = [
    { id: Role.User, name: 'user', description: 'Just a normal user' },
    { id: Role.Admin, name: 'admin', description: 'Can do everything' },
    { id: Role.Manager, name: 'manager', description: 'Like a user, but can manage site settings' },
    {
        id: Role.Billing,
        name: 'billing',
        description: 'Cannot view content, but can change users and storage',
    },
];

export class User {
    readonly id: string;
    name: string;
    readonly email: string;
    role: string;
    picture: string;
    sub: string;
    avatar: string;
    readonly is_staff: boolean;
    is_owner: boolean;
    readonly permissions: string[];
    readonly email_hash: string;

    hasPermission(name: string): boolean {
        return this.permissions.includes(name);
    }

    public getRole(): Role {
        switch (this.role) {
            case 'admin':
                return Role.Admin;
            case 'billing':
                return Role.Billing;
            case 'manager':
                return Role.Manager;
            default:
                return Role.User;
        }
    }
}

@Injectable({
    providedIn: 'root',
})
export class UserService {
    public user: ReplaySubject<User>;
    public users: BehaviorSubject<User[]>;

    constructor(
        private clientService: ClientService,
        private http: HttpClient,
    ) {
        this.user = new ReplaySubject<User>(1);
        this.users = new BehaviorSubject<User[]>([]);

        this.clientService.client
            .pipe(
                map((client) => client.id),
                switchMap((clientId) =>
                    this.http.get<User[]>(`/api/workspace/client/${clientId}/members/`),
                ),
            )
            .subscribe((users) => this.users.next(users));
    }

    updateUser(userId: string, name: string): Observable<User> {
        const url = `/api/auth/user/${userId}/`;
        const options = {
            name,
            withCredentials: true,
        };
        return this.http.patch(url, options).pipe(map((result) => deserialize(User, result)));
    }

    assignRole(userId: string, roleId: number): Observable<User> {
        const url = `/api/auth/user/${userId}/assignRole/`;
        const options = {
            role: roleId,
            withCredentials: true,
        };
        return this.http.post(url, options).pipe(map((result) => deserialize(User, result)));
    }
}
